"use client"

import { useState } from "react"
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  styled,
} from "@mui/material"
import {
  AccessTime as ClockIcon,
  LocationOn as MapPinIcon,
  Phone as PhoneIcon,
  Email as MailIcon,
  Language as GlobeIcon,
  LocalShipping as TruckIcon,
  AttachMoney as DollarSignIcon,
  ShoppingBag as ShoppingBagIcon,
  Timer as TimerIcon,
  Event as CalendarIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Map as MapIcon,
} from "@mui/icons-material"
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material"
import EditProviderDialog from "./edit-provider-dialog"
import axios from "axios"
import { useNavigate } from "react-router-dom"

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
  height: "100%",
}))

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  "& .MuiChip-label": {
    fontWeight: 500,
  },
}))

const InfoCard = styled(Card)(({ theme }) => ({
  height: "100%",
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)",
  borderRadius: theme.shape.borderRadius * 1.5,
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px 0 rgba(0,0,0,0.1)",
  },
}))

const InfoCardContent = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  height: "100%",
}))

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 48,
  height: 48,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
}))

const ProviderHeader = ({ provider, onUpdate }) => {
  const navigate = useNavigate()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)

  const handleDelete = async () => {
    setDeleteDialogOpen(false)
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/providers/deleteOneUser`, { id: provider._id });
      navigate('/providers');
    } catch (error) {
      console.error("Error deleting provider:", error);
    }
  }

  const handleEdit = () => {
    setEditDialogOpen(true)
  }

  const handleEditSave = (updatedProvider) => {
    setEditDialogOpen(false)
    onUpdate(updatedProvider)
  }

  const contactInfo = [
    {
      icon: <MapPinIcon />,
      title: "Address",
      content: `${provider.address}, ${provider.postcode}  ${provider.place !== undefined && provider.place !== null ? provider.place :""}, ${provider.region}`,
    },
    {
      icon: <MapIcon />,
      title: "Coordinates",
      content: provider.latitude && provider.longitude
        ? `${provider.latitude.toFixed(6)}, ${provider.longitude.toFixed(6)}`
        : "Not specified",
    },
    { icon: <PhoneIcon />, title: "Phone", content: provider.telephone },
    { icon: <PhoneIcon />, title: "Mobile", content: provider.mobile },
    { icon: <MailIcon />, title: "Email", content: provider.email },
    {
      icon: <GlobeIcon />,
      title: "Website",
      content: (
        <Link href={provider.domain} target="_blank" rel="noopener noreferrer" underline="hover">
          {provider.domain}
        </Link>
      ),
    },
    { icon: <BusinessIcon />, title: "Branch", content: provider.branch },
    { icon: <CategoryIcon />, title: "Category", content: provider.category },
    { icon: <DescriptionIcon />, title: "Description", content: provider.description },
    { icon: <GavelIcon />, title: "Imprint", content: provider.Imprint },
    {
        icon: <ClockIcon />,
        title: "Opening Hours",
        content: `${provider.openTime || "Not specified"} - ${provider.closeTime || "Not specified"}`,
      },
      {
        icon: <CalendarIcon />,
        title: "Order Period",
        content:
          provider.orderStartDay && provider.orderEndDay
            ? `${provider.orderStartDay} - ${provider.orderEndDay}`
            : "Not specified",
      },
      { icon: <TimerIcon />, title: "Delivery Time", content: provider.deliveryApproxTime || "Not specified" },
      { icon: <TruckIcon />, title: "Delivery Area", content: provider.deliveryCircle || "Not specified" },
      {
        icon: <DollarSignIcon />,
        title: "Delivery Cost",
        content: provider.deliveryCost === 0 ? "Free" : `€${provider.deliveryCost}`,
      },
      {
        icon: <ShoppingBagIcon />,
        title: "Min. Order",
        content: provider.minOrderCost === 0 ? "None" : `€${provider.minOrderCost}`,
      },
  ]

  

  return (
    <Grid container spacing={3}>
      {/* Main Info Card */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Box sx={{ position: "relative" }}>
              <Box sx={{ position: "absolute", right: 8, top: 8, display: "flex", gap: 1, zIndex: 1 }}>
                <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={handleEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={() => setDeleteDialogOpen(true)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Box
                    component="img"
                    src={provider.logo?.url.startsWith("http") ?`${provider?.logo?.url}`:`https://www.mein-vendoapp.de:3001${provider?.logo?.url}`}
                    alt={provider?.providerName}
                    sx={{
                      width: "100%",
                      height: "200px",
                      borderRadius: 2,
                      objectFit: "cover",
                      aspectRatio: "1/1",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={9}>
                  <Typography variant="h4" gutterBottom fontWeight="bold">
                    {provider.providerName}
                  </Typography>
                  <Typography color="text.secondary" paragraph>
                    {provider.description}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {provider.branch && <StyledChip label={provider.branch} variant="outlined" />}
                    {provider.paypalMode && <StyledChip label="PayPal Accepted" variant="outlined" color="primary" />}
                    {provider.cashMode && <StyledChip label="Cash Accepted" variant="outlined" color="primary" />}
                    {provider.Abholung && <StyledChip label="Pickup Available" variant="outlined" color="success" />}
                    {provider.Lieferung && <StyledChip label="Delivery Available" variant="outlined" color="success" />}
                    <StyledChip
                      label={provider.availability ? "Available" : "Unavailable"}
                      color={provider.availability ? "success" : "error"}
                    />
                    {provider.deactivate && <StyledChip label="Deactivated" color="error" />}
                    {provider.iswelcome && <StyledChip label="Welcome" color="primary" />}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Contact Information Cards */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
          Provider Information
        </Typography>
        <Grid container spacing={3}>
          {contactInfo.map((info, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <InfoCard>
                <InfoCardContent>
                  <IconWrapper>{info.icon}</IconWrapper>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {info.title}
                  </Typography>
                  <Typography color="text.secondary">{info.content}</Typography>
                </InfoCardContent>
              </InfoCard>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this provider? This action cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <EditProviderDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleEditSave}
        provider={provider}
      />
    </Grid>
  )
}

export default ProviderHeader

